import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import {
  ExternalLink, X, RefreshCw, Filter, Check, ChevronRight, ArrowLeft, Zap, Trash2, AlertTriangle,
} from "lucide-react";
import { ScreenTitle } from "@/components/ScreenTitle";
import { useNavigate } from "react-router-dom";

const AUTHORS = [
  { id: 1,  short: "Actualités",     color: "bg-[var(--accent)] text-white" },
  { id: 10, short: "Politique",      color: "bg-[#3f6e4a] text-white" },
  { id: 18, short: "International",  color: "bg-[#2a5788] text-white" },
];

const LEVEL_DOT = {
  green: "bg-[var(--status-success)]",
  orange: "bg-[var(--accent)]",
  red: "bg-[var(--accent-strong)]",
};
const LEVEL_LABEL = { green: "vert", orange: "orange", red: "rouge" };

// A draft is "safe" (easy to relire) when:
//   - status pending
//   - risk_level = green
//   - source not flagged as incomplete
//   - title has 8-12 words (charter rule)
//   - has a featured image (never publish without image)
function wordCount(title) {
  return (title || "").trim().split(/\s+/).filter((w) => /[A-Za-zÀ-ÿ]/.test(w)).length;
}
function isSafe(d) {
  if (!d || d.status !== "pending") return false;
  if (d.risk_level !== "green") return false;
  if (d.needs_verification) return false;
  if (!d.featured_media_id) return false;
  const n = wordCount(d.title);
  return n >= 8 && n <= 12;
}

export default function QueuePage() {
  const { currentId: siteId, current: currentSite } = useSite();
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("pending"); // pending | all
  const [sort, setSort] = useState("recent"); // recent | risk
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(10);
  const [refreshProgress, setRefreshProgress] = useState(null); // { start, current, target }
  const [cmdOpen, setCmdOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  const purgeStale = async () => {
    if (purging) return;
    if (!window.confirm(
      "Supprimer tous les brouillons ‘en attente’ plus vieux que 24 h ? " +
      "Ils seront également retirés de WordPress."
    )) return;
    setPurging(true);
    try {
      const r = await api.post("/drafts/purge-stale", null, {
        params: { hours: 24, delete_wp: true },
      });
      toast.success(
        `${r.data.deleted} brouillon(s) supprimé(s)` +
        (r.data.wp_deleted ? ` — ${r.data.wp_deleted} côté WP` : "")
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge impossible");
    } finally { setPurging(false); }
  };

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get("/drafts", {
          params: {
            limit: 200, sort,
            ...(filter === "pending" ? { status: "pending" } : {}),
            ...(siteId ? { site_id: siteId } : {}),
          },
        }),
        api.get("/stats"),
      ]);
      setDrafts(d.data);
      setStats(s.data);
    } catch (e) { /* interceptor */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort, siteId]);

  const publish = async (id) => {
    setPublishing(id);
    try {
      await api.post(`/drafts/${id}/publish`);
      toast.success("Publié sur WordPress");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication échouée");
    } finally { setPublishing(null); }
  };

  const reject = async (id) => {
    if (!window.confirm("Rejeter ce brouillon ? Il sera déplacé dans la corbeille WordPress.")) return;
    setRejecting(id);
    try {
      await api.post(`/drafts/${id}/reject`);
      toast.success("Brouillon rejeté");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejet impossible");
    } finally { setRejecting(null); }
  };

  const switchToEmergent = async () => {
    if (!window.confirm(
      "Basculer le LLM vers l'API Emergent (crédits internes) ?\n\n" +
      "À utiliser en dépannage quand ta clé Anthropic est vide. Tu peux repasser " +
      "sur ta clé Anthropic depuis Réglages > Configuration une fois rechargée."
    )) return;
    try {
      await api.put("/config", { llm_provider: "emergent" });
      toast.success("LLM basculé sur Emergent. Relance une génération pour tester.");
      load();
    } catch (e) {
      toast.error("Bascule impossible : " + (e?.response?.data?.detail || "erreur"));
    }
  };

  // Trigger a manual fetch across all active feeds — bypasses the hourly quota.
  const refreshFeeds = async () => {
    if (refreshing) return;
    setRefreshing(true);
    const target = refreshCount;
    try {
      const r = await api.post("/process/refresh", null, {
        params: { max_items: target, ...(siteId ? { site_id: siteId } : {}) },
      });
      const startPending = stats?.drafts_pending ?? 0;
      setRefreshProgress({ start: startPending, current: 0, target: r.data.feeds ? target : target });
      toast.success(`Génération lancée — jusqu'à ${target} nouveau(x) brouillon(s) sur ${r.data.feeds} flux.`);
      // Poll the list every 3 seconds to reveal new drafts.
      const t0 = Date.now();
      const poll = async () => {
        const [d, s] = await Promise.all([
          api.get("/drafts", { params: { limit: 200, sort, ...(filter === "pending" ? { status: "pending" } : {}), ...(siteId ? { site_id: siteId } : {}) } }),
          api.get("/stats"),
        ]);
        setDrafts(d.data); setStats(s.data);
        const created = Math.max(0, (s.data.drafts_pending ?? 0) - startPending);
        setRefreshProgress({ start: startPending, current: created, target });
        // Stop after target reached, or after 3 minutes, whichever first.
        if (created >= target || Date.now() - t0 > 180000) {
          setRefreshing(false);
          setTimeout(() => setRefreshProgress(null), 3000);
        } else {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer la génération");
      setRefreshing(false);
      setRefreshProgress(null);
    }
  };

  // ---- Grouping ----
  const { safeDrafts, otherDrafts } = useMemo(() => {
    const safe = [];
    const other = [];
    for (const d of drafts) {
      if (isSafe(d)) safe.push(d);
      else other.push(d);
    }
    return { safeDrafts: safe, otherDrafts: other };
  }, [drafts]);

  const quotaText = stats
    ? `${stats.drafts_last_hour}/${stats.current_quota} · plage ${stats.current_range}`
    : "—";
  const quotaPct = stats
    ? Math.min(100, (stats.drafts_last_hour / Math.max(1, stats.current_quota)) * 100)
    : 0;

  return (
    <div data-testid="queue-page">
      <ScreenTitle
        eyebrow={`File d'attente · ${stats?.drafts_pending ?? "—"} en attente`}
        title="À relire"
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={purgeStale}
              disabled={purging}
              data-testid="queue-purge-stale"
              title="Supprimer les brouillons ‘en attente’ vieux de plus de 24 h"
              className="min-h-[44px] px-3 flex items-center gap-2 border border-[var(--border-default)] bg-white hover:bg-[var(--accent-strong)] hover:text-white hover:border-[var(--accent-strong)] text-[11px] uppercase tracking-widest font-mono transition-colors disabled:opacity-60"
              aria-label="Purger les vieux brouillons"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden md:inline">{purging ? "Purge…" : "Purger > 24 h"}</span>
            </button>
            <button
              onClick={() => setCmdOpen(true)}
              data-testid="queue-command"
              title="Commander un article à partir d'une URL"
              className="min-h-[44px] px-3 flex items-center gap-2 border border-black bg-white hover:bg-black hover:text-white text-[11px] uppercase tracking-widest font-mono transition-colors"
              aria-label="Commander un article"
            >
              <Zap className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Commander</span>
            </button>
            <div className="flex items-stretch border border-black bg-white overflow-hidden">
              <button
                onClick={refreshFeeds}
                disabled={refreshing}
                data-testid="queue-refresh"
                title={`Générer ${refreshCount} brouillon(s) tout de suite`}
                className="min-h-[44px] px-3 flex items-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.75} />
                <span className="hidden sm:inline">
                  {refreshing ? "Génération…" : `Générer ${refreshCount}`}
                </span>
              </button>
              <select
                value={refreshCount}
                onChange={(e) => setRefreshCount(parseInt(e.target.value, 10))}
                disabled={refreshing}
                data-testid="queue-refresh-count"
                title="Nombre d'articles à générer"
                aria-label="Nombre d'articles à générer"
                className="min-h-[44px] px-2 text-[11px] font-mono bg-white border-l border-[var(--border-default)] focus:outline-none disabled:opacity-60 cursor-pointer"
              >
                <option value={5}>×5</option>
                <option value={10}>×10</option>
                <option value={25}>×25</option>
                <option value={50}>×50</option>
              </select>
            </div>
          </div>
        }
      />

      {refreshProgress && (
        <ProgressBar
          data-testid="refresh-progress"
          current={refreshProgress.current}
          target={refreshProgress.target}
          done={!refreshing}
        />
      )}

      {/* LLM health banner — credit exhausted or generic error */}
      {stats?.llm_health && stats.llm_health.status !== "ok" && (
        <div
          className={`mx-5 mb-3 border-2 p-3 flex items-start gap-3 ${
            stats.llm_health.status === "credit_exhausted"
              ? "border-[var(--accent-strong)] bg-red-50"
              : "border-amber-500 bg-amber-50"
          }`}
          data-testid="llm-health-banner"
        >
          <AlertTriangle
            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
              stats.llm_health.status === "credit_exhausted"
                ? "text-[var(--accent-strong)]"
                : "text-amber-700"
            }`}
            strokeWidth={1.75}
          />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-mono uppercase tracking-widest text-[11px] mb-1">
              {stats.llm_health.status === "credit_exhausted"
                ? "LLM bloqué · crédit Anthropic épuisé"
                : "LLM en erreur"}
              {" · "}
              <span className="text-[var(--text-muted)]">
                provider {stats.llm_health.provider} · {stats.llm_health.model}
              </span>
            </div>
            {stats.llm_health.status === "credit_exhausted" ? (
              <p className="leading-snug">
                Ton compte Anthropic n&apos;a plus de crédits. Aucun brouillon ne peut être généré
                tant que ce n&apos;est pas rechargé. Deux options :
              </p>
            ) : (
              <p className="leading-snug">
                Dernière erreur : <b>{stats.llm_health.last_error_message}</b>. Consulte
                le Journal pour les détails.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <a
                href="https://console.anthropic.com/settings/billing"
                target="_blank" rel="noreferrer noopener"
                data-testid="llm-open-billing"
                className="min-h-[36px] px-3 flex items-center gap-1 border border-black bg-white hover:bg-black hover:text-white text-[11px] font-mono uppercase tracking-widest"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Recharger Anthropic
              </a>
              {stats.llm_health.provider === "anthropic" && (
                <button
                  onClick={switchToEmergent}
                  data-testid="llm-switch-emergent"
                  className="min-h-[36px] px-3 flex items-center gap-1 border border-[var(--accent)] bg-[var(--accent)] text-white hover:bg-[var(--accent-strong)] hover:border-[var(--accent-strong)] text-[11px] font-mono uppercase tracking-widest"
                >
                  <Zap className="w-3.5 h-3.5" /> Basculer sur Emergent
                </button>
              )}
              <Link
                to="/journal"
                data-testid="llm-open-logs"
                className="min-h-[36px] px-3 flex items-center gap-1 border border-[var(--border-default)] hover:border-black text-[11px] font-mono uppercase tracking-widest"
              >
                Voir le Journal
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Quota gauge */}
      <div className="px-5 pb-3" data-testid="queue-quota">
        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
          <span>Quota horaire</span>
          <span>{quotaText}</span>
        </div>
        <div className="h-[3px] bg-[var(--bg-sidebar)] w-full overflow-hidden">
          <div className="h-full bg-black transition-[width]" style={{ width: `${quotaPct}%` }} />
        </div>
      </div>

      {/* Filter row */}
      <div className="px-5 py-3 flex items-center gap-2 text-xs font-mono">
        <Filter className="w-3.5 h-3.5 text-[var(--text-muted)]" strokeWidth={1.75} />
        <FilterChip active={filter === "pending"} onClick={() => setFilter("pending")} testid="filter-pending">
          À relire
        </FilterChip>
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
          Tous
        </FilterChip>
        <div className="ml-auto flex items-center gap-2">
          <SortChip active={sort === "risk"} onClick={() => setSort("risk")} testid="sort-risk">
            risque
          </SortChip>
          <SortChip active={sort === "recent"} onClick={() => setSort("recent")} testid="sort-recent">
            récents
          </SortChip>
        </div>
      </div>

      {/* Empty */}
      {!loading && drafts.length === 0 && <EmptyState filter={filter} />}

      {/* Safe drafts section — appears only when there is at least one safe draft in pending mode */}
      {filter === "pending" && safeDrafts.length > 0 && (
        <SafeSection
          drafts={safeDrafts}
          onPublish={publish}
          onReject={reject}
          onOpenBatch={() => setBatchOpen(true)}
          publishing={publishing}
          rejecting={rejecting}
        />
      )}

      {/* Other drafts */}
      {otherDrafts.length > 0 && (
        <div data-testid="queue-list-others">
          {filter === "pending" && safeDrafts.length > 0 && (
            <SectionRule label="Le reste — relecture individuelle requise" />
          )}
          {otherDrafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              onPublish={publish}
              onReject={reject}
              publishing={publishing === d.id}
              rejecting={rejecting === d.id}
            />
          ))}
        </div>
      )}

      {/* Command modal */}
      {cmdOpen && (
        <CommandArticleModal
          onClose={() => setCmdOpen(false)}
          onCreated={() => { setCmdOpen(false); load(); }}
        />
      )}

      {/* Batch confirmation modal */}
      {batchOpen && (
        <BatchConfirmModal
          drafts={safeDrafts}
          onClose={() => setBatchOpen(false)}
          running={batchRunning}
          onConfirm={async () => {
            setBatchRunning(true);
            try {
              const ids = safeDrafts.map((d) => d.id);
              const r = await api.post("/drafts/batch-publish", { ids });
              const { published, total } = r.data;
              if (published === total) {
                toast.success(`${published} brouillon(s) publié(s)`);
              } else {
                toast.warning(`${published}/${total} publié(s) — voir le journal pour les échecs`);
              }
              setBatchOpen(false);
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Publication en lot échouée");
            } finally { setBatchRunning(false); }
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// SafeSection — "À relire rapidement"
// -----------------------------------------------------------------------
function SafeSection({ drafts, onPublish, onReject, onOpenBatch, publishing, rejecting }) {
  return (
    <section
      className="bg-white border-t border-b border-[var(--border-default)] mb-2"
      data-testid="safe-section"
    >
      <div className="px-5 py-4 flex items-start gap-3">
        <span className="inline-block w-2 h-2 rounded-full mt-2 bg-[var(--status-success)]" />
        <div className="flex-1 min-w-0">
          <div className="caption mb-1">À relire rapidement</div>
          <div className="font-headings text-xl leading-tight">
            {drafts.length} brouillon{drafts.length > 1 ? "s" : ""} à faible risque
          </div>
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary)] mt-1">
            Score vert · source complète · titre 8-12 mots. Publie individuellement,
            ou passe par l&apos;écran de confirmation ci-dessous.
          </p>
        </div>
      </div>

      {/* Title list — plain, tap opens detail */}
      <div className="border-t border-[var(--border-default)]" data-testid="safe-list">
        {drafts.map((d) => (
          <SafeRow
            key={d.id}
            draft={d}
            onPublish={onPublish}
            onReject={onReject}
            publishing={publishing === d.id}
            rejecting={rejecting === d.id}
          />
        ))}
      </div>

      {/* Batch CTA */}
      <div className="px-5 py-4 border-t border-[var(--border-default)]">
        <button
          onClick={onOpenBatch}
          data-testid="open-batch-confirm"
          className="w-full min-h-[52px] border border-black text-sm uppercase tracking-widest font-mono hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-2"
        >
          Passer à l&apos;écran de confirmation ({drafts.length})
          <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
        </button>
        <p className="mt-2 text-[11px] font-mono text-[var(--text-muted)] text-center">
          Aucune publication n&apos;est déclenchée avant confirmation explicite.
        </p>
      </div>
    </section>
  );
}

function SafeRow({ draft, onPublish, onReject, publishing, rejecting }) {
  const time = new Date(draft.created_at).toLocaleTimeString("fr-FR", {
    hour: "2-digit", minute: "2-digit",
  });
  return (
    <div
      className="px-5 py-3 border-b border-[var(--border-default)] last:border-b-0 flex items-center gap-3"
      data-testid={`safe-row-${draft.id}`}
    >
      <div className="flex-1 min-w-0">
        <Link
          to={`/brouillon/${draft.id}`}
          data-testid={`safe-title-${draft.id}`}
          className="block font-headings text-[16px] leading-snug hover:opacity-70 transition-opacity"
        >
          {draft.title}
        </Link>
        <div className="text-[11px] font-mono text-[var(--text-muted)] mt-0.5 truncate">
          {draft.feed_name} · {time} · {wordCount(draft.title)} mots
        </div>
      </div>
      <button
        onClick={() => onPublish(draft.id)}
        disabled={publishing}
        data-testid={`safe-publish-${draft.id}`}
        className="min-h-[44px] px-3 bg-black text-white text-[11px] font-medium uppercase tracking-widest hover:bg-[#222] transition-colors disabled:opacity-60"
      >
        {publishing ? "…" : "Publier"}
      </button>
      <button
        onClick={() => onReject(draft.id)}
        disabled={rejecting}
        data-testid={`safe-reject-${draft.id}`}
        className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-[var(--border-default)] hover:border-[var(--accent-strong)] hover:text-[var(--accent-strong)] transition-colors disabled:opacity-60"
        aria-label="Rejeter"
      >
        <X className="w-4 h-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}

function SectionRule({ label }) {
  return (
    <div className="px-5 pt-6 pb-3 flex items-center gap-3">
      <div className="h-[1px] bg-[var(--border-default)] flex-1" />
      <span className="caption whitespace-nowrap">{label}</span>
      <div className="h-[1px] bg-[var(--border-default)] flex-1" />
    </div>
  );
}

// -----------------------------------------------------------------------
// Standard draft card (used for "other" drafts)
// -----------------------------------------------------------------------
function DraftCard({ draft, onPublish, onReject, publishing, rejecting }) {
  const time = new Date(draft.created_at).toLocaleTimeString("fr-FR", {
    hour: "2-digit", minute: "2-digit",
  });
  const dot = LEVEL_DOT[draft.risk_level] || "bg-[var(--text-muted)]";
  const chapoPreview = (draft.chapo || "").slice(0, 180);

  return (
    <article
      className="px-5 py-5 border-b border-[var(--border-default)] last:border-b-0"
      data-testid={`queue-card-${draft.id}`}
    >
      <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--text-muted)] mb-2 flex-wrap">
        <span className={`inline-block w-2 h-2 rounded-full ${dot}`} aria-hidden />
        <span className="text-[var(--text-secondary)]">{draft.feed_name}</span>
        <span>·</span>
        <span>{time}</span>
        <span>·</span>
        <RiskBadge level={draft.risk_level} count={draft.risk_score} />
        {draft.needs_verification && (
          <>
            <span>·</span>
            <span
              className="text-[var(--accent)] italic"
              data-testid={`needs-verif-${draft.id}`}
            >
              à vérifier
            </span>
          </>
        )}
        {!draft.featured_media_id && draft.status === "pending" && (
          <>
            <span>·</span>
            <span
              className="text-[var(--accent-strong)]"
              data-testid={`no-image-${draft.id}`}
            >
              sans image
            </span>
          </>
        )}
        {draft.status === "published" && (
          <>
            <span>·</span>
            <span className="text-[var(--status-success)]">publié</span>
          </>
        )}
        {draft.status === "rejected" && (
          <>
            <span>·</span>
            <span className="text-[var(--text-muted)] line-through">rejeté</span>
          </>
        )}
      </div>

      <Link
        to={`/brouillon/${draft.id}`}
        data-testid={`queue-title-${draft.id}`}
        className="block group"
      >
        <h2 className="font-headings text-[22px] leading-[1.15] tracking-tight text-black group-hover:opacity-80 transition-opacity">
          {draft.title}
        </h2>
        {chapoPreview && (
          <p className="mt-2 text-[14px] leading-relaxed text-[var(--text-secondary)] line-clamp-2">
            {chapoPreview}
            {(draft.chapo || "").length > 180 ? "…" : ""}
          </p>
        )}
      </Link>

      {draft.status === "pending" && (
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => {
              if (!draft.featured_media_id) {
                toast.error("Ajoute une image de couverture avant de publier");
                return;
              }
              onPublish(draft.id);
            }}
            disabled={publishing || !draft.featured_media_id}
            title={!draft.featured_media_id ? "Image de couverture requise" : undefined}
            data-testid={`publish-${draft.id}`}
            className="min-h-[44px] px-5 bg-black text-white text-[12px] font-medium uppercase tracking-[0.15em] hover:bg-[#222] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {publishing ? "Publication…" : "Publier"}
          </button>
          {draft.wp_edit_url && (
            <a
              href={draft.wp_edit_url}
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-[var(--border-default)] hover:border-black transition-colors"
              aria-label="Ouvrir dans WordPress"
              data-testid={`open-wp-${draft.id}`}
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.75} />
            </a>
          )}
          <button
            onClick={() => onReject(draft.id)}
            disabled={rejecting}
            data-testid={`reject-${draft.id}`}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-[var(--border-default)] hover:border-[var(--accent-strong)] hover:text-[var(--accent-strong)] transition-colors disabled:opacity-60"
            aria-label="Rejeter"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      )}
    </article>
  );
}

function RiskBadge({ level, count }) {
  const label = LEVEL_LABEL[level] || "—";
  const cls =
    level === "red"
      ? "text-[var(--accent-strong)]"
      : level === "orange"
        ? "text-[var(--accent)]"
        : "text-[var(--status-success)]";
  return (
    <span className={cls}>
      risque {label} · {count}
    </span>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`min-h-[36px] px-3 border font-mono uppercase tracking-wider text-[11px] transition-colors ${
        active
          ? "border-black bg-black text-white"
          : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-black"
      }`}
    >
      {children}
    </button>
  );
}

function SortChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`min-h-[36px] px-2.5 font-mono uppercase tracking-wider text-[11px] transition-colors ${
        active ? "text-black underline underline-offset-4" : "text-[var(--text-muted)] hover:text-black"
      }`}
    >
      {children}
    </button>
  );
}


function ProgressBar({ current, target, done }) {
  const pct = Math.min(100, Math.round((current / Math.max(1, target)) * 100));
  const label = done
    ? `${current} brouillon(s) créé(s) sur ${target} demandés`
    : current === 0
      ? `Génération en cours… (0/${target})`
      : `${current}/${target} brouillon(s) prêt(s) — les nouveaux apparaissent en haut de la liste`;
  return (
    <div
      className="mx-4 sm:mx-6 mt-2 mb-1 border border-black bg-white overflow-hidden"
      data-testid="refresh-progress"
    >
      <div className="px-3 py-2 flex items-center gap-3">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-secondary)] flex-1 truncate">
          {label}
        </span>
        <span className="text-[11px] font-mono font-semibold shrink-0">{pct}%</span>
      </div>
      <div className="h-1.5 w-full bg-[var(--bg-sidebar)]">
        <div
          className="h-full bg-black transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


function EmptyState({ filter }) {
  return (
    <div className="px-5 py-20 text-center" data-testid="queue-empty">
      <div className="font-headings text-2xl mb-2">Rien à relire.</div>
      <p className="text-sm text-[var(--text-secondary)] max-w-xs mx-auto leading-relaxed">
        {filter === "pending"
          ? "Le pipeline n'a encore rien poussé, ou tout a été traité. Ajoute un flux ou lance un traitement manuel depuis l'onglet Flux."
          : "Aucun brouillon enregistré pour le moment."}
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------
// Batch confirmation modal — mandatory review step before publish
// -----------------------------------------------------------------------
function BatchConfirmModal({ drafts, onClose, onConfirm, running }) {
  return (
    <div
      className="fixed inset-0 z-[100001] bg-white flex flex-col"
      data-testid="batch-confirm-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="border-b border-[var(--border-default)]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={onClose}
            data-testid="batch-close"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-[var(--bg-sidebar)]"
            aria-label="Annuler"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <div className="flex-1 text-center text-[11px] font-mono uppercase tracking-wider text-[var(--text-muted)]">
            Confirmation de publication
          </div>
          <div className="w-11" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto pb-[110px]">
        <div className="max-w-2xl mx-auto">
          <div className="px-5 pt-6 pb-4">
            <div className="caption mb-1">Lot à publier</div>
            <h1 className="font-headings text-3xl leading-none tracking-tight">
              {drafts.length} titre{drafts.length > 1 ? "s" : ""} à valider
            </h1>
            <p className="mt-3 text-sm text-[var(--text-secondary)] leading-relaxed">
              Vérifie chaque titre ci-dessous. Un tap sur un titre ouvre le détail complet.
              La publication ne démarre qu&apos;après le bouton « Confirmer » en bas de l&apos;écran.
            </p>
          </div>

          <ol className="border-t border-[var(--border-default)]" data-testid="batch-list">
            {drafts.map((d, i) => (
              <li
                key={d.id}
                className="px-5 py-4 border-b border-[var(--border-default)]"
                data-testid={`batch-item-${d.id}`}
              >
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[11px] text-[var(--text-muted)] w-6 pt-1 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/brouillon/${d.id}`}
                      data-testid={`batch-open-${d.id}`}
                      className="font-headings text-[17px] leading-snug block hover:opacity-70"
                    >
                      {d.title}
                    </Link>
                    <div className="text-[11px] font-mono text-[var(--text-muted)] mt-1 truncate">
                      {d.feed_name} · {wordCount(d.title)} mots ·
                      {" "}risque vert · source complète
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Sticky footer */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[100002] bg-white border-t border-[var(--border-default)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={onClose}
            data-testid="batch-cancel"
            className="min-h-[48px] px-5 border border-[var(--border-default)] text-sm uppercase tracking-widest font-mono hover:border-black transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={running || drafts.length === 0}
            data-testid="batch-confirm"
            className="flex-1 min-h-[48px] bg-black text-white text-sm uppercase tracking-widest font-medium hover:bg-[#222] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Check className="w-4 h-4" strokeWidth={1.75} />
            {running ? "Publication…" : `Confirmer et publier (${drafts.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------- Command Article Modal --------------------------

function CommandArticleModal({ onClose, onCreated }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [authorId, setAuthorId] = useState(1);
  const [catId, setCatId] = useState("");
  const [running, setRunning] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("URL requise");
      return;
    }
    setRunning(true);
    try {
      const r = await api.post("/commands/article", {
        url: url.trim(),
        wp_author_id: authorId,
        wp_category_id: catId ? parseInt(catId, 10) : null,
      });
      toast.success(`Brouillon créé — ${r.data.title.slice(0, 60)}…`);
      onCreated();
      if (r.data.draft_id) navigate(`/draft/${r.data.draft_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de générer l'article");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="command-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-white border-t sm:border border-[var(--border-default)] max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-2 border-b border-[var(--border-default)] px-4 py-3">
          <Zap className="w-4 h-4" strokeWidth={1.75} />
          <div className="text-[11px] font-mono uppercase tracking-wider flex-1">
            Commander un article
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="command-close"
            className="min-h-[36px] min-w-[36px] flex items-center justify-center hover:bg-[var(--bg-sidebar)]"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-[11px] font-mono text-[var(--text-secondary)] bg-[var(--bg-sidebar)] border-l-2 border-[var(--accent)] px-3 py-2 leading-relaxed">
            Colle l&apos;URL d&apos;une dépêche ou d&apos;un article existant.
            AIMKA extrait le contenu, applique ton prompt de réécriture puis
            dépose le brouillon dans WordPress (aucune publication automatique).
            Les paywalls et pages nécessitant du JavaScript peuvent échouer.
          </div>

          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1.5">
              URL source
            </label>
            <input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
              placeholder="https://apnews.com/article/…"
              data-testid="command-url-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </div>

          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1.5">
              Service auteur
            </label>
            <div className="grid grid-cols-3 gap-2" data-testid="command-author-picker">
              {AUTHORS.map((a) => {
                const active = authorId === a.id;
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => setAuthorId(a.id)}
                    data-testid={`command-author-${a.id}`}
                    className={`min-h-[44px] px-2 text-[11px] uppercase tracking-wider font-mono border transition-colors ${
                      active
                        ? `${a.color} border-black`
                        : "bg-white border-[var(--border-default)] hover:border-black"
                    }`}
                  >
                    {active ? "✓ " : ""}
                    {a.short}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-1.5">
              Catégorie WordPress · ID (optionnel)
            </label>
            <input
              type="number"
              value={catId}
              onChange={(e) => setCatId(e.target.value)}
              placeholder="ex : 12"
              data-testid="command-cat-input"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
            />
          </div>
        </div>

        <div className="border-t border-[var(--border-default)] px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            data-testid="command-cancel"
            className="min-h-[48px] px-4 border border-[var(--border-default)] hover:border-black text-[11px] uppercase tracking-widest font-mono transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={running || !url.trim()}
            data-testid="command-submit"
            className="flex-1 min-h-[48px] bg-black text-white text-sm uppercase tracking-widest font-medium hover:bg-[#222] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Zap className="w-4 h-4" strokeWidth={1.75} />
            {running ? "Génération en cours (30-60 s)…" : "Générer le brouillon"}
          </button>
        </div>
      </form>
    </div>
  );
}

